import React, {useRef} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {DrawerItem} from '../../interfaces/router/drawerItem';
import DrawerMainList from '../drawerMainList';
import {DrawerContentComponentProps} from '@react-navigation/drawer';
import Icon from '../icon';
import {tr} from '../../translations';
import TextGeneric from '../textGeneric';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import screens from '../../config/screens';
import {endSession} from '../../screens/auth/login/actions';
import {getSettingByKey, openUrl} from '../../utils/helpers';
import {RootState} from '../../redux/store';
import {useSelector} from 'react-redux';
import Config from 'react-native-config';
import {DrawerActions} from '@react-navigation/native';
import ProgressiveImage from '../progressiveImage';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import switchTheme from 'react-native-theme-switch-animation';
import {View} from 'react-native';
import {toggleSpecialityModal} from '../../connected-components/specialityModal/actions';
import {toggleSettingsModal} from '../../connected-components/settingsModal/actions';
import Support from '../../screens/content/support';
import Profile from '../../screens/content/profile';
import {modalTypes, SettingKey} from '../../utils/enums';
import {setDarkMode} from '../../screens/content/settings/action';
import {isWeb} from '../../utils/responsive';
import {openQuestionModal} from '../../connected-components/questionModal/actions';

const Container = styled.ScrollView<{
  $topInset?: number;
  paddingVertical?: number;
}>`
  width: 100%;
  height: 100%;
  background-color: ${({theme}) => theme.homeBackground};
  padding-vertical: ${({paddingVertical}) => `${paddingVertical || 0}px`};
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  letter-spacing: 1px;
  text-align: left;
  width: 100%;
  padding-top: 7%;
  padding-bottom: 2%;
  padding-horizontal: 5%;
`;
const Row = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-horizontal: 5%;
`;
const MenuIconContainer = styled.TouchableOpacity`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  padding: ${({theme}) => theme.text.s5 * 0.7}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;
const DarkIconContainer = styled(MenuIconContainer)<{darkMode: boolean}>`
  margin-horizontal: ${({theme}) => theme.text.s10}px;
  background-color: ${({theme, darkMode}) =>
    darkMode ? theme.primary : theme.secondary};
`;
const ProfileContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  flex: 1;
  padding-right: 10%;
`;
const ProfileImageContainer = styled.View`
  border-radius: 100px;
  width: ${({theme}) => theme.text.s1 * 1.3}px;
  aspect-ratio: 1;
  overflow: hidden;
  border-color: ${({theme}) => theme.text.light};
  border-width: 3px;
`;
const ProfileImage = styled(ProgressiveImage)`
  width: 100%;
  height: 100%;
`;
const ProfileContentContainer = styled.View`
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;
const Name = styled(TextGeneric)<{fontSize: number}>`
  font-size: ${({theme}) => theme.text.s7}px;
  padding-horizontal: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  text-transform: capitalize;
  flex-wrap: wrap;
  max-width: 100%;
  ${isWeb &&
  'overflow-wrap: break-word; word-wrap: break-word; word-break: break-word;'}
`;
const Spetiality = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  padding-horizontal: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark + 'CC'};
`;

/**
 * SideMenu component that represents a side menu with a main list and details view.
 * @component
 * @param {Object} props - The SideMenu component props.
 * @returns {React.ReactElement} The rendered SideMenu component.
 */
const SideMenu = ({navigation}: DrawerContentComponentProps) => {
  const {top: topInset} = useSafeAreaInsets();
  const {hdp, getByScreenSize, isMobile} = useResponsiveDimensions();
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  const {currentUser, config} =
    useSelector((state: RootState) => state.auth) || {};
  const {darkMode} = useSelector((state: RootState) => state.settings) || {};
  const viewRef = useRef<any>(null);

  let speciality = config?.specialties?.find(
    s => s.id === currentUser?.specialty_id,
  );

  // Calculate paddingVertical
  const paddingVertical = Math.max(topInset || 0, hdp(5)) + hdp(1);

  const handleUrls = (name: string, url: string) => {
    if (isWeb) {
      navigation?.navigate(screens.content.fileViewer, {
        title: tr(`drawer.${name}`),
        fileUrl: url,
      });
      return;
    }
    openUrl(url);
  };

  /**
   * Event handler for item press in the main side menu.
   * @function
   * @param {DrawerItem} item - The selected item.
   * @returns {void}
   */
  const onItemPress = (item: DrawerItem) => {
    try {
      switch (item.name) {
        case screens.content.logout: {
          openQuestionModal({
            title: tr('login.logoutTitle'),
            message: tr('login.logoutInfo'),
            onConfirm: () => {
              endSession();
            },
            confirmText: tr('login.signOut'),
            rejectText: tr('app.cancel'),
            type: modalTypes.question,
          });
          return;
        }
        case screens.content.privacyPolicy: {
          handleUrls(
            item.name,
            getSettingByKey(SettingKey.PrivacyPolicy) || Config.POLICY,
          );
          return;
        }
        case screens.content.privacyPolicyForm: {
          handleUrls(
            item.name,
            getSettingByKey(SettingKey.PrivacyPolicyForm) || Config.POLICY_FORM,
          );
          return;
        }
        case screens.content.termsConditions: {
          handleUrls(
            item.name,
            getSettingByKey(SettingKey.TermsConditions) ||
              Config.TERMS_AND_CONDITIONS,
          );
          return;
        }
        case screens.content.chooseYourSpeciality: {
          toggleSpecialityModal();
          return;
        }
        case screens.content.settings: {
          toggleSettingsModal();
          return;
        }
        case screens.content.customerSupport: {
          toggleSettingsModal({
            title: tr(`drawer.${screens.content.customerSupport}`),
            component: () => <Support />,
          });
          return;
        }
        case screens.homeTabs: {
          navigation?.navigate(screens.homeTabs, {
            screens: screens.content.homeTab,
          });
          navigation?.goBack();
          // @ts-ignore
          navigation?.pop(10);
          navigation?.closeDrawer();
          return;
        }
        default: {
          navigation?.closeDrawer();
          navigation?.navigate(item.name);
        }
      }
    } catch (error) {
    }
  };

  const toggleDarkThemeHandler = () => {
    if (isWeb) {
      setDarkMode({darkMode: !darkMode, switchThumbRef: viewRef});
    } else {
      viewRef?.current?.measure(
        async (x1: any, y1: any, width: any, height: any, px: any, py: any) => {
          switchTheme({
            switchThemeFunction: () => {
              setDarkMode({darkMode: !darkMode, switchThumbRef: viewRef});
            },
            animationConfig: {
              type: 'circular',
              duration: 400,
              startingPoint: {
                cy: py + height / 2,
                cx: px + width / 2,
              },
            },
          });
        },
      );
    }
    return;
  };

  return (
    <Container paddingVertical={paddingVertical}>
      <Row
        style={{
          flexDirection: isMobile ? 'row' : 'column',
          alignItems: isMobile ? 'center' : 'flex-start',
        }}>
        <ProfileContainer
          disabled={Config.DEMO_EMAIL === currentUser?.email}
          style={{
            opacity: Config.DEMO_EMAIL === currentUser?.email ? 0 : 1,
          }}
          activeOpacity={0.8}
          onPress={() =>
            toggleSettingsModal({
              title: tr('profile.title'),
              component: () => <Profile />,
            })
          }>
          <ProfileImageContainer>
            <ProfileImage
              source={{
                uri: currentUser?.image_url,
              }}
              defaultImage={require('../../assets/images/user.png')}
            />
          </ProfileImageContainer>
          <ProfileContentContainer>
            <Name
              numberOfLines={2}
              bold
              fontSize={getByScreenSize(theme.text.s7, theme.text.s8)}>
              {currentUser?.first_name + ' ' + currentUser?.last_name}
            </Name>
            {(currentUser?.email || '').trim().toLowerCase() !==
              Config.APPLE_EMAIL && (
              <Spetiality semiBold>{speciality?.name || ''}</Spetiality>
            )}
          </ProfileContentContainer>
        </ProfileContainer>
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            alignSelf: 'flex-end',
            paddingTop: isMobile ? 0 : theme.text.s3,
          }}>
          <DarkIconContainer
            darkMode={darkMode}
            //@ts-ignore
            ref={viewRef}
            activeOpacity={0.8}
            onPress={toggleDarkThemeHandler}>
            <Icon
              name={darkMode ? 'lightMode' : 'darkMode'}
              color={theme.text.light}
              size={getByScreenSize(theme.text.s5, theme.text.s8)}
            />
          </DarkIconContainer>
          <MenuIconContainer
            activeOpacity={0.8}
            onPress={() => navigation?.dispatch(DrawerActions.toggleDrawer())}>
            <Icon
              name={'x'}
              color={theme.secondary}
              size={getByScreenSize(theme.text.s5, theme.text.s8)}
            />
          </MenuIconContainer>
        </View>
      </Row>
      <Title bold pointerEvents={'none'}>
        {tr('drawer.title')}
      </Title>
      <DrawerMainList onItemPress={onItemPress} />
    </Container>
  );
};

export default SideMenu;
